import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { useVehicleReports } from "@/hooks/use-fleet-reports";
import { useAuth } from "@/hooks/use-auth";
import { useReportExport } from "@/hooks/use-report-export";
import { Eye, Car, Fuel, Wrench, TrendingUp, Activity, Gauge, DollarSign, AlertTriangle } from "lucide-react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, AreaChart, Area } from "recharts";
import { ReportHeader, KPICard, KPIGrid, ChartContainer, AdvancedFilters, FilterValues, TrendIndicator } from "@/components/fleet/reports";

export function VehicleReports() {
  const { activeCompany, companies } = useAuth();
  const navigate = useNavigate();
  const { slug } = useParams<{ slug: string }>();
  const location = useLocation();
  const [filters, setFilters] = useState<FilterValues>({});
  
  const company = companies.find(c => c.slug === slug);
  const effectiveCompanyId = activeCompany?.id || company?.id;
  
  const { data: vehicleReports, isLoading, error } = useVehicleReports(effectiveCompanyId);
  const { exportReport, isExporting } = useReportExport();

  const isAdminArea = location.pathname.includes('/admin/');

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardHeader className="pb-2">
                <div className="h-4 bg-muted rounded w-3/4"></div>
                <div className="h-8 bg-muted rounded w-1/2"></div>
              </CardHeader>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (error || !vehicleReports) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-center text-muted-foreground">Erro ao carregar dados dos veículos.</p>
        </CardContent>
      </Card>
    );
  }

  // Filter reports (using available fields from the data)
  const filteredReports = vehicleReports;

  // Calculate metrics
  const totalVehicles = filteredReports.length;
  const totalDistance = filteredReports.reduce((sum, v) => sum + v.total_distance, 0);
  const totalFuelCost = filteredReports.reduce((sum, v) => sum + v.total_fuel_cost, 0);
  const totalMaintenanceCost = filteredReports.reduce((sum, v) => sum + v.total_maintenance_cost, 0);
  const totalCost = totalFuelCost + totalMaintenanceCost;
  const avgFuelEfficiency = totalVehicles > 0 
    ? filteredReports.reduce((sum, v) => sum + v.fuel_efficiency, 0) / totalVehicles 
    : 0;
  const avgCostPerKm = totalDistance > 0 ? totalCost / totalDistance : 0;
  const activeVehicles = totalVehicles; // All vehicles shown are active
  const inMaintenance = 0;
  const avgTripsPerVehicle = totalVehicles > 0
    ? filteredReports.reduce((sum, v) => sum + v.total_trips, 0) / totalVehicles
    : 0;

  // Chart data
  const topVehiclesByDistance = filteredReports
    .sort((a, b) => b.total_distance - a.total_distance)
    .slice(0, 10)
    .map(v => ({
      name: v.vehicle_plate,
      distance: v.total_distance,
      fuel_cost: v.total_fuel_cost,
      maintenance_cost: v.total_maintenance_cost
    }));

  const topVehiclesByCost = filteredReports
    .sort((a, b) => (b.total_fuel_cost + b.total_maintenance_cost) - (a.total_fuel_cost + a.total_maintenance_cost))
    .slice(0, 10)
    .map(v => ({
      name: v.vehicle_plate,
      cost: v.total_fuel_cost + v.total_maintenance_cost
    }));

  const costDistribution = [
    { name: "Combustível", value: totalFuelCost, color: "hsl(var(--primary))" },
    { name: "Manutenção", value: totalMaintenanceCost, color: "hsl(var(--secondary))" }
  ];

  // Group by model as category proxy
  const efficiencyByCategory = Object.entries(
    filteredReports.reduce((acc, v) => {
      const cat = v.vehicle_model || 'Outros';
      if (!acc[cat]) acc[cat] = { total: 0, count: 0 };
      acc[cat].total += v.fuel_efficiency || 0;
      acc[cat].count++;
      return acc;
    }, {} as Record<string, { total: number; count: number }>)
  ).map(([name, data]) => ({
    name,
    efficiency: data.count > 0 ? data.total / data.count : 0
  })).slice(0, 5);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatDistance = (km: number) => {
    return `${km.toLocaleString('pt-BR', { maximumFractionDigits: 0 })} km`;
  };


  const handleExport = async (format: 'excel' | 'pdf') => {
    await exportReport({
      data: filteredReports || [],
      type: 'vehicles',
      format: format,
      company: activeCompany,
      filename: `relatorio-veiculos-${Date.now()}.${format === 'pdf' ? 'pdf' : 'xlsx'}`
    });
  };

  return (
    <div className="space-y-6">
      <ReportHeader 
        title="Relatórios de Veículos"
        subtitle="Análise detalhada da performance e custos por veículo"
        onExportPDF={() => handleExport('pdf')}
        onExportExcel={() => handleExport('excel')}
        onPrint={() => window.print()}
      />

      <div className="mb-6">
        <AdvancedFilters
          config={{
            status: [
              { label: 'Ativo', value: 'active' },
              { label: 'Inativo', value: 'inactive' },
              { label: 'Manutenção', value: 'maintenance' }
            ],
            category: [
              { label: 'Carro', value: 'car' },
              { label: 'Caminhão', value: 'truck' },
              { label: 'Van', value: 'van' },
              { label: 'Moto', value: 'motorcycle' }
            ]
          }}
          onFilterChange={setFilters}
          initialFilters={filters}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total de Veículos</CardTitle>
            <Car className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalVehicles}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Distância Total</CardTitle>
            <Gauge className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatDistance(totalDistance)}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Custo Total</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(totalCost)}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Custo Combustível</CardTitle>
            <Fuel className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(totalFuelCost)}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Custo Manutenção</CardTitle>
            <Wrench className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(totalMaintenanceCost)}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Custo por Km</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(avgCostPerKm)}</div>
            <p className="text-xs text-muted-foreground">Média da frota</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Eficiência Média</CardTitle>
            <Fuel className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{avgFuelEfficiency.toFixed(1)} km/l</div>
            <p className="text-xs text-muted-foreground">{avgTripsPerVehicle.toFixed(1)} viagens/veículo</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Veículos Ativos</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{activeVehicles}</div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartContainer title="Top 10 Veículos por Distância" description="Veículos com maior quilometragem">
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={topVehiclesByDistance}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip 
                formatter={(value, name) => [
                  name === 'distance' ? formatDistance(value as number) : formatCurrency(value as number),
                  name === 'distance' ? 'Distância' : name === 'fuel_cost' ? 'Combustível' : 'Manutenção'
                ]}
              />
              <Bar dataKey="distance" fill="hsl(var(--primary))" />
            </BarChart>
          </ResponsiveContainer>
        </ChartContainer>

        <ChartContainer title="Top 10 Veículos por Custo" description="Veículos com maior impacto financeiro">
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={topVehiclesByCost}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip formatter={(value) => formatCurrency(value as number)} />
              <Bar dataKey="cost" fill="hsl(var(--chart-2))" />
            </BarChart>
          </ResponsiveContainer>
        </ChartContainer>

        <ChartContainer title="Distribuição de Custos" description="Proporção entre combustível e manutenção">
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={costDistribution}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {costDistribution.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip formatter={(value) => formatCurrency(value as number)} />
            </PieChart>
          </ResponsiveContainer>
        </ChartContainer>

        <ChartContainer title="Eficiência por Categoria" description="Média de km/L por tipo de veículo">
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={efficiencyByCategory}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip formatter={(value) => `${(value as number).toFixed(1)} km/L`} />
              <Bar dataKey="efficiency" fill="hsl(var(--chart-3))" />
            </BarChart>
          </ResponsiveContainer>
        </ChartContainer>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Detalhamento por Veículo</CardTitle>
          <CardDescription>Dados detalhados de performance e custos</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Veículo</TableHead>
                <TableHead>Modelo</TableHead>
                <TableHead className="text-right">Viagens</TableHead>
                <TableHead className="text-right">Distância</TableHead>
                <TableHead className="text-right">Combustível</TableHead>
                <TableHead className="text-right">Manutenção</TableHead>
                <TableHead className="text-right">Eficiência</TableHead>
                <TableHead className="text-center">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredReports.map((vehicle) => (
                <TableRow key={vehicle.vehicle_id}>
                  <TableCell className="font-medium">{vehicle.vehicle_plate}</TableCell>
                  <TableCell>{vehicle.vehicle_model}</TableCell>
                  <TableCell className="text-right">{vehicle.total_trips}</TableCell>
                  <TableCell className="text-right">{formatDistance(vehicle.total_distance)}</TableCell>
                  <TableCell className="text-right">{formatCurrency(vehicle.total_fuel_cost)}</TableCell>
                  <TableCell className="text-right">{formatCurrency(vehicle.total_maintenance_cost)}</TableCell>
                  <TableCell className="text-right">
                    {vehicle.fuel_efficiency > 0 ? `${vehicle.fuel_efficiency.toFixed(1)} km/L` : 'N/A'}
                  </TableCell>
                  <TableCell className="text-center">
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => {
                        const prefix = isAdminArea ? 'admin' : 'user';
                        navigate(`/company/${slug}/${prefix}/systems/fleetone/reports/vehicles/${vehicle.vehicle_id}`);
                      }}
                      disabled={!vehicle.vehicle_id}
                    >
                      <Eye className="h-4 w-4 mr-2" />
                      Ver Detalhes
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
